import { useState, useEffect } from 'react';

export interface ModalState {
  showLoginModal: boolean;
  showRegisterModal: boolean;
  showSettingsModal: boolean;
  showAboutModal: boolean;
  showPricingModal: boolean;
  showFAQModal: boolean;
  showContactModal: boolean;
  showMiniMapModal: boolean;
  showLearnMoreModal: boolean;
  showDisclaimerModal: boolean;
  showRestaurantPreferencesModal: boolean;
  showSubscriptionManagementModal: boolean;
  showTierChangeConfirmationModal: boolean;
  showSuggestedMealModal: boolean;
  showReportNonRestaurantModal: boolean;
  suggestedMealMode: boolean;
}

export interface ModalActions {
  setShowLoginModal: (show: boolean) => void;
  setShowRegisterModal: (show: boolean) => void;
  setShowSettingsModal: (show: boolean) => void;
  setShowAboutModal: (show: boolean) => void;
  setShowPricingModal: (show: boolean) => void;
  setShowFAQModal: (show: boolean) => void;
  setShowContactModal: (show: boolean) => void;
  setShowMiniMapModal: (show: boolean) => void;
  setShowLearnMoreModal: (show: boolean) => void;
  setShowDisclaimerModal: (show: boolean) => void;
  setShowRestaurantPreferencesModal: (show: boolean) => void;
  setShowSubscriptionManagementModal: (show: boolean) => void;
  setShowTierChangeConfirmationModal: (show: boolean) => void;
  setShowSuggestedMealModal: (show: boolean) => void;
  setShowReportNonRestaurantModal: (show: boolean) => void;
  setSuggestedMealMode: (mode: boolean) => void;
  openLoginModal: () => void;
  openRegisterModal: () => void;
  openSettingsModal: () => void;
  openAboutModal: () => void;
  openPricingModal: () => void;
  openFAQModal: () => void;
  openContactModal: () => void;
  openMiniMapModal: () => void;
  openLearnMoreModal: () => void;
  openDisclaimerModal: () => void;
  openRestaurantPreferencesModal: () => void;
  openSubscriptionManagementModal: () => void;
  openTierChangeConfirmationModal: () => void;
  openSuggestedMealModal: () => void;
  openReportNonRestaurantModal: () => void;
  enterSuggestedMealMode: () => void;
  exitSuggestedMealMode: () => void;
  closeLoginModal: () => void;
  closeRegisterModal: () => void;
  closeSettingsModal: () => void;
  closeAboutModal: () => void;
  closePricingModal: () => void;
  closeFAQModal: () => void;
  closeContactModal: () => void;
  closeMiniMapModal: () => void;
  closeLearnMoreModal: () => void;
  closeDisclaimerModal: () => void;
  closeRestaurantPreferencesModal: () => void;
  closeSubscriptionManagementModal: () => void;
  closeTierChangeConfirmationModal: () => void;
  closeSuggestedMealModal: () => void;
  closeReportNonRestaurantModal: () => void;
  closeAllModals: () => void;
}

export const useModalManager = (): ModalState & ModalActions => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showMiniMapModal, setShowMiniMapModal] = useState(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showRestaurantPreferencesModal, setShowRestaurantPreferencesModal] = useState(false);
  const [showSubscriptionManagementModal, setShowSubscriptionManagementModal] = useState(false);
  const [showTierChangeConfirmationModal, setShowTierChangeConfirmationModal] = useState(false);
  const [showSuggestedMealModal, setShowSuggestedMealModal] = useState(false);
  const [showReportNonRestaurantModal, setShowReportNonRestaurantModal] = useState(false);
  const [suggestedMealMode, setSuggestedMealMode] = useState(false);

  // Convenience functions to open modals
  const openLoginModal = () => setShowLoginModal(true);
  const openRegisterModal = () => setShowRegisterModal(true);
  const openSettingsModal = () => setShowSettingsModal(true);
  const openAboutModal = () => setShowAboutModal(true);
  const openPricingModal = () => setShowPricingModal(true);
  const openFAQModal = () => setShowFAQModal(true);
  const openContactModal = () => setShowContactModal(true);
  const openMiniMapModal = () => setShowMiniMapModal(true);
  const openLearnMoreModal = () => setShowLearnMoreModal(true);
  const openDisclaimerModal = () => setShowDisclaimerModal(true);
  const openRestaurantPreferencesModal = () => setShowRestaurantPreferencesModal(true);
  const openSubscriptionManagementModal = () => setShowSubscriptionManagementModal(true);
  const openTierChangeConfirmationModal = () => setShowTierChangeConfirmationModal(true);
  const openSuggestedMealModal = () => setShowSuggestedMealModal(true);
  const openReportNonRestaurantModal = () => setShowReportNonRestaurantModal(true);
  const enterSuggestedMealMode = () => setSuggestedMealMode(true);
  const exitSuggestedMealMode = () => setSuggestedMealMode(false);

  // Convenience functions to close modals
  const closeLoginModal = () => setShowLoginModal(false);
  const closeRegisterModal = () => setShowRegisterModal(false);
  const closeSettingsModal = () => setShowSettingsModal(false);
  const closeAboutModal = () => setShowAboutModal(false);
  const closePricingModal = () => setShowPricingModal(false);
  const closeFAQModal = () => setShowFAQModal(false);
  const closeContactModal = () => setShowContactModal(false);
  const closeMiniMapModal = () => setShowMiniMapModal(false);
  const closeLearnMoreModal = () => setShowLearnMoreModal(false);
  const closeDisclaimerModal = () => setShowDisclaimerModal(false);
  const closeRestaurantPreferencesModal = () => setShowRestaurantPreferencesModal(false);
  const closeSubscriptionManagementModal = () => setShowSubscriptionManagementModal(false);
  const closeTierChangeConfirmationModal = () => setShowTierChangeConfirmationModal(false);
  const closeSuggestedMealModal = () => setShowSuggestedMealModal(false);
  const closeReportNonRestaurantModal = () => setShowReportNonRestaurantModal(false);

  // Close all modals
  const closeAllModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
    setShowSettingsModal(false);
    setShowAboutModal(false);
    setShowPricingModal(false);
    setShowFAQModal(false);
    setShowContactModal(false);
    setShowMiniMapModal(false);
    setShowLearnMoreModal(false);
    setShowDisclaimerModal(false);
    setShowRestaurantPreferencesModal(false);
    setShowSubscriptionManagementModal(false);
    setShowTierChangeConfirmationModal(false);
    setShowSuggestedMealModal(false);
    setShowReportNonRestaurantModal(false);
    setSuggestedMealMode(false);
  };

  return {
    // State
    showLoginModal,
    showRegisterModal,
    showSettingsModal,
    showAboutModal,
    showPricingModal,
    showFAQModal,
    showContactModal,
    showMiniMapModal,
    showLearnMoreModal,
    showDisclaimerModal,
    showRestaurantPreferencesModal,
    showSubscriptionManagementModal,
    showTierChangeConfirmationModal,
    showSuggestedMealModal,
    showReportNonRestaurantModal,
    suggestedMealMode,
    
    // Actions
    setShowLoginModal,
    setShowRegisterModal,
    setShowSettingsModal,
    setShowAboutModal,
    setShowPricingModal,
    setShowFAQModal,
    setShowContactModal,
    setShowMiniMapModal,
    setShowLearnMoreModal,
    setShowDisclaimerModal,
    setShowRestaurantPreferencesModal,
    setShowSubscriptionManagementModal,
    setShowTierChangeConfirmationModal,
    setShowSuggestedMealModal,
    setShowReportNonRestaurantModal,
    setSuggestedMealMode,
    openLoginModal,
    openRegisterModal,
    openSettingsModal,
    openAboutModal,
    openPricingModal,
    openFAQModal,
    openContactModal,
    openMiniMapModal,
    openLearnMoreModal,
    openDisclaimerModal,
    openRestaurantPreferencesModal,
    openSubscriptionManagementModal,
    openTierChangeConfirmationModal,
    openSuggestedMealModal,
    openReportNonRestaurantModal,
    enterSuggestedMealMode,
    exitSuggestedMealMode,
    closeLoginModal,
    closeRegisterModal,
    closeSettingsModal,
    closeAboutModal,
    closePricingModal,
    closeFAQModal,
    closeContactModal,
    closeMiniMapModal,
    closeLearnMoreModal,
    closeDisclaimerModal,
    closeRestaurantPreferencesModal,
    closeSubscriptionManagementModal,
    closeTierChangeConfirmationModal,
    closeSuggestedMealModal,
    closeReportNonRestaurantModal,
    closeAllModals,
  };
};